// @flow
import { createReducer, createAction } from '@reduxjs/toolkit';
import moment from 'moment';
import axios from 'utils/axios';

type MeetingStatus = 'delayed' | 'confirmed' | 'canceled' | 'not_attended';

const getMeetingType = (status: MeetingStatus) => {
  return status === 'not_attended' ? 'pending' : status;
};

// ACTIONS
export const fetchMeetingsStarted = createAction('home_meetings/fetch_started');

export const createMeetingStarted = createAction(
  'home_meetings/create_started'
);

export const createMeetingSucceeded = createAction(
  'home_meetings/create_succeeded'
);

export const resetIsCreatedMeeting = createAction(
  'home_meetings/reset_is_created_meeting'
);

export const fetchMeetingsSucceeded = createAction(
  'home_meetings/fetch_succeeded'
);

export const fetchMeetingsFound = createAction('home_meetings/found');

export const fetchMeetingsFailed = createAction('home_meetings/fetch_failed');

export const findMeetingStarted = createAction('home_meetings/find_started');

export const findMeetingSucceeded = createAction(
  'home_meetings/find_succeeded'
);

export const findMeetingFailed = createAction('home_meetings/find_failed');

export const updateMeetingStarted = createAction(
  'home_meetings/update_started'
);

export const updateMeetingSucceeded = createAction(
  'home_meetings/update_succeeded'
);

export const updateMeetingFailed = createAction('home_meetings/update_failed');

export const meetingSelected = createAction('home_meetings/select');

export const selectNextPendingMeeting = createAction(
  'home_meetings/select_next_pending'
);

export const resetState = createAction('home_meetings/reset');

export const fetchTodayHomeMeetingsStarted = createAction(
  'home_meetings/fetch_today_by_home_started'
);

export const fetchTodayHomeMeetingsSucceeded = createAction(
  'home_meetings/fetch_today_by_home_succeeded'
);

export const fetchTodayHomeMeetingsFailed = createAction(
  'home_meetings/fetch_today_by_home_failed'
);

export const assignMeetingSuccess = createAction(
  'home_meetings/assign_success'
);
export const assignMeetingFailed = createAction('home_meetings/assign_failed');
export const resetAssignState = createAction('home_meetings/assign_reset');

export function assignCustomerSuccessMeeting(meetingId, superHeroEmail) {
  return async (dispatch: any) => {
    try {
      const result = await axios.put(
        `/home_meetings/${meetingId}/assign_customer_success`,
        { email: superHeroEmail }
      );
      dispatch(assignMeetingSuccess(result.data.homeMeetings));
    } catch (e) {
      dispatch(assignMeetingFailed(e.message));
    }
  };
}

// ACTION_CREATORS
export function createMeeting(paramsToCreate: Object) {
  return async (dispatch: any) => {
    try {
      dispatch(createMeetingStarted());

      const {
        data: { homeMeeting }
      } = await axios.post('/home_meetings', paramsToCreate);

      dispatch(createMeetingSucceeded(homeMeeting));
    } catch (e) {
      dispatch(fetchMeetingsFailed(e.message));
    }
  };
}

export function fetchMeetingsByFilter(term: string) {
  return async (dispatch: any) => {
    dispatch(fetchMeetingsStarted());

    const filters = {
      term: term
    };

    try {
      const result = await axios.get('/home_meetings', {
        params: { filterParams: filters }
      });

      dispatch(fetchMeetingsSucceeded(result.data.homeMeetings));
    } catch (e) {
      dispatch(fetchMeetingsFailed(e.message));
    }
  };
}

export function fetchCustomerSuccessSchedule(filterParams: any) {
  return async (dispatch: any) => {
    dispatch(fetchMeetingsStarted());

    try {
      const result = await axios.get('/home_meetings', {
        params: {
          filterParams: filterParams
        }
      });

      dispatch(fetchMeetingsSucceeded(result.data.homeMeetings));
    } catch (e) {
      dispatch(fetchMeetingsFailed(e.message));
    }
  };
}

export function fetchMeetingsByStatusAndFilter(
  status: MeetingStatus,
  dates = [moment({ hour: 9 }), moment({ hour: 21 })]
) {
  return async (dispatch: any) => {
    dispatch(fetchMeetingsStarted());

    const filters = {
      type: getMeetingType(status),
      beginningDateTime: moment(dates[0])
        .set('hour', 9)
        .toISOString(),
      endingDateTime: moment(dates[1])
        .set('hour', 21)
        .toISOString()
    };

    try {
      const result = await axios.get('/home_meetings', {
        params: { filterParams: filters }
      });
      dispatch(fetchMeetingsSucceeded(result.data.homeMeetings));
    } catch (e) {
      dispatch(fetchMeetingsFailed(e.message));
    }
  };
}

export function findMeetingById(meetingId: string) {
  return async (dispatch: any) => {
    dispatch(findMeetingStarted());

    try {
      const result = await axios.get(`/home_meetings/${meetingId}`);

      dispatch(
        findMeetingSucceeded({
          meeting: result.data.homeMeeting,
          meta: result.data.meta
        })
      );
    } catch (e) {
      dispatch(findMeetingFailed(e.message));
    }
  };
}

export function assignCustomerSuccess(
  meetingId: string,
  superHeroEmail: string
) {
  return async (dispatch: any) => {
    dispatch(updateMeetingStarted());
    try {
      const result = await axios.put(
        `/home_meetings/${meetingId}/assign_customer_success`,
        { data: { email: superHeroEmail } }
      );

      dispatch(updateMeetingSucceeded(result.data.homeMeeting));
    } catch (e) {
      dispatch(updateMeetingFailed(e.message));
    }
  };
}

export function confirmMeeting(meetingId: string) {
  return async (dispatch: any) => {
    dispatch(updateMeetingStarted());

    try {
      const result = await axios.put(`/home_meetings/${meetingId}/confirm`);

      dispatch(
        updateMeetingSucceeded({
          meetingId: result.data.homeMeeting.id,
          operation: 'confirm'
        })
      );
    } catch (e) {
      dispatch(updateMeetingFailed(e));
    }
  };
}

export function cancelMeeting(
  meetingId: string,
  reason: string,
  canceledByUserId: string
) {
  return async (dispatch: any) => {
    dispatch(updateMeetingStarted());

    try {
      await axios.delete(`/home_meetings/destroy`, {
        data: {
          id: meetingId,
          canceledOption: reason,
          canceledById: canceledByUserId
        }
      });

      dispatch(updateMeetingSucceeded({ meetingId, operation: 'cancel' }));
    } catch (e) {
      dispatch(updateMeetingFailed(e.message));
    }
  };
}

export function fetchTodayHomeMeetings(homeId: string) {
  return async (dispatch: any) => {
    try {
      dispatch(fetchTodayHomeMeetingsStarted());

      const { data } = await axios.get(`/homes/${homeId}/home_meetings`, {
        params: {
          beginning_date_time: moment()
            .startOf('day')
            .toISOString(),
          ending_date_time: moment()
            .endOf('day')
            .toISOString()
        }
      });

      dispatch(fetchTodayHomeMeetingsSucceeded(data));
    } catch (exception) {
      dispatch(fetchTodayHomeMeetingsFailed());
    }
  };
}

export function rescheduleMeeting(
  meetingId: string,
  date: string,
  time: string,
  canceledByUserId: string
) {
  return async (dispatch: any) => {
    dispatch(updateMeetingStarted());

    try {
      await axios.put(`/home_meetings/${meetingId}`, {
        homeMeeting: { date, time },
        canceledById: canceledByUserId
      });
      dispatch(updateMeetingSucceeded({ meetingId, operation: 'reschedule' }));
    } catch (e) {
      dispatch(updateMeetingFailed(e.message));
    }
  };
}

// REDUCER
const INITIAL_STATE = {
  homeMeetings: [],
  currentMeeting: undefined,
  selectedMeeting: undefined,
  isFetchingMeetings: false,
  isFindingMeeting: true,
  isUpdatingMeeting: false,
  isCreatingMeeting: false,
  isCreatedMeeting: false,
  isFindingMeetingPeople: false,
  lastConfirmedMeeting: undefined,
  lastCanceledMeeting: undefined,
  tenantReasonsToCancel: [],
  ownerReasonsToCancel: [],
  homeMeetingsFound: [],
  isFetchingTodayHomeMeetings: false,
  todayHomeMeetings: [],
  isInProgress: false,
  isFailRequest: false,
  createdMeeting: undefined,
  isFetchingTodayHomeMeetingsTeemLeader: false,
  todayHomeMeetingsTeamLeader: []
};

export default createReducer(INITIAL_STATE, {
  [fetchMeetingsStarted]: state => {
    state.isFetchingMeetings = true;
    state.homeMeetings = [];
    state.currentMeeting = undefined;
    state.selectedMeeting = undefined;
  },
  [createMeetingStarted]: state => {
    state.isCreatingMeeting = true;
    state.isCreatedMeeting = false;
  },
  [createMeetingSucceeded]: (state, { payload }) => {
    state.isCreatingMeeting = false;
    state.isCreatedMeeting = true;
    state.createdMeeting = payload;
  },
  [resetIsCreatedMeeting]: state => {
    state.isCreatedMeeting = false;
  },
  [fetchMeetingsFound]: (state, action) => {
    state.isFetchingMeetings = false;
    state.homeMeetingsFound = action.payload;
  },
  [fetchMeetingsSucceeded]: (state, action) => {
    state.isFetchingMeetings = false;
    state.homeMeetings = action.payload;
  },
  [fetchMeetingsFailed]: state => {
    state.isFetchingMeetings = false;
  },
  [findMeetingStarted]: state => {
    state.isFindingMeeting = true;
    state.currentMeeting = undefined;
  },
  [findMeetingSucceeded]: (state, action) => {
    state.isFindingMeeting = false;
    state.currentMeeting = action.payload.meeting;
    state.ownerReasonsToCancel = action.payload.meta.whyNotAttendedReasonsOwner;
    state.tenantReasonsToCancel =
      action.payload.meta.whyNotAttendedReasonsTenant;
  },
  [findMeetingFailed]: (state, action) => {
    state.isFindingMeeting = false;
  },
  [updateMeetingStarted]: state => {
    state.isUpdatingMeeting = true;
    state.lastCanceledMeeting = undefined;
    state.lastConfirmedMeeting = undefined;
    state.isFailRequest = false;
  },
  [updateMeetingSucceeded]: (state, action) => {
    state.isUpdatingMeeting = false;
    state.isFailRequest = false;
    if (action.payload.operation === 'confirm') {
      state.lastConfirmedMeeting = action.payload.meetingId;
    }

    if (action.payload.operation === 'cancel') {
      state.lastCanceledMeeting = action.payload.meetingId;
    }

    const indexToRemove = state.homeMeetings.findIndex(
      meeting => meeting.id === action.payload.meetingId
    );
    state.homeMeetings.splice(indexToRemove, 1);
  },
  [updateMeetingFailed]: state => {
    state.isUpdatingMeeting = false;
    state.isFailRequest = true;
  },
  [meetingSelected]: (state, action) => {
    state.selectedMeeting = action.payload;
  },
  [selectNextPendingMeeting]: state => {
    const meetings = [...state.homeMeetings].sort(
      (firstMeeting, secondMeeting) => {
        return (
          firstMeeting.isReassinged ||
          moment(firstMeeting.dateTime).isBefore(moment(secondMeeting.dateTime))
        );
      }
    );

    state.selectedMeeting = meetings.length > 0 ? meetings[0].id : undefined;
  },
  [resetState]: () => INITIAL_STATE,
  [fetchTodayHomeMeetingsStarted]: state => {
    state.isFetchingTodayHomeMeetings = true;
  },
  [fetchTodayHomeMeetingsSucceeded]: (state, { payload: { homeMeetings } }) => {
    state.todayHomeMeetings = homeMeetings;
    state.isFetchingTodayHomeMeetings = false;
  },
  [fetchTodayHomeMeetingsFailed]: state => {
    state.isFetchingTodayHomeMeetings = false;
  },
  [resetAssignState]: (state, action) => {
    state.isUpdatingMeeting = false;
    state.isFailRequest = false;
  },
  [assignMeetingSuccess]: (state, action) => {
    state.isUpdatingMeeting = true;
    state.isFailRequest = false;
  },
  [assignMeetingFailed]: (state, action) => {
    state.isUpdatingMeeting = false;
    state.isFailRequest = true;
  }
});
